import Layout from '@/components/feature/Layout';
import SEOHead from '@/components/base/SEOHead';
import { localBusinessSchema, buildBreadcrumbSchema, buildGraphSchema } from '@/components/base/schemas';
import { Link } from 'react-router-dom';

const breadcrumbSchema = buildBreadcrumbSchema([
  { name: 'Accueil', item: 'https://fenetre-paris15.fr/' },
  { name: 'Blog', item: 'https://fenetre-paris15.fr/blog/' },
]);

const articles = [
  {
    slug: '/blog/reparer-poignee-fenetre-qui-tourne-dans-le-vide/',
    title: 'Poignée de fenêtre qui tourne dans le vide : causes et solutions',
    excerpt: `Votre poignée de fenêtre tourne sans résistance et la fenêtre ne s'ouvre ni ne se ferme plus ? C'est un problème fréquent qui peut avoir plusieurs causes. Découvrez comment diagnostiquer la panne et quand faire appel à un professionnel.`,
    date: '12 juin 2026',
    service: 'Fenêtres PVC',
    serviceLink: '/reparation-fenetre-pvc-paris-15/',
  },
  {
    slug: '/blog/volet-roulant-bloque-causes-solutions/',
    title: 'Volet roulant bloqué : 6 causes et leurs solutions',
    excerpt: `Un volet roulant qui refuse de monter ou de descendre est un problème à la fois agaçant et potentiellement dangereux. Voici les 6 causes les plus fréquentes de blocage et comment les résoudre, que votre volet soit manuel ou motorisé.`,
    date: '28 mai 2026',
    service: 'Volets roulants',
    serviceLink: '/reparation-volet-roulant-paris-15/',
  },
  {
    slug: '/blog/double-vitrage-embue-que-faire/',
    title: 'Double vitrage embué : que faire et peut-on le réparer ?',
    excerpt: `De la buée entre les deux vitres de votre fenêtre ? C'est le signe que le joint d'étanchéité du vitrage isolant est défaillant. Explications, diagnostic et solutions pour retrouver des fenêtres claires.`,
    date: '3 mai 2026',
    service: 'Fenêtres PVC / Aluminium',
    serviceLink: '/reparation-fenetre-pvc-paris-15/',
  },
];

export default function BlogIndexPage() {
  return (
    <Layout>
      <SEOHead
        title="Blog Réparation Fenêtre Paris 15 — Conseils | DF PRO"
        description="Conseils et articles sur la réparation de fenêtres, volets roulants et baies vitrées dans Paris 15ᵉ. Guides pratiques par DF PRO. ☎ 01 87 66 81 55"
        canonicalUrl="https://fenetre-paris15.fr/blog/"
        schema={buildGraphSchema(localBusinessSchema, breadcrumbSchema)}
      />

      <div className="bg-brand-mint/30 py-3">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <nav aria-label="Fil d'Ariane" className="text-xs text-brand-text/60">
            <Link to="/" className="hover:text-brand-accent">Accueil</Link><span className="mx-2">/</span><span className="text-brand-accent font-medium">Blog</span>
          </nav>
        </div>
      </div>

      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <h1 className="font-heading text-3xl md:text-5xl font-bold text-brand-dark text-center mb-4">Blog DF PRO</h1>
          <p className="text-brand-text/70 text-center mb-6 max-w-3xl mx-auto text-base leading-relaxed">
            Bienvenue sur le blog de DF PRO, votre expert en réparation de fenêtres et menuiseries à Paris 15ᵉ. Nous partageons ici notre savoir-faire et nos conseils d'experts pour vous aider à mieux comprendre l'entretien et la réparation de vos fenêtres, volets roulants et baies vitrées. Apprenez à reconnaître les pannes courantes, découvrez quand il est préférable de réparer plutôt que de remplacer, et bénéficiez de diagnostics précis pour chaque situation. Nos articles sont rédigés par des professionnels qui interviennent chaque jour dans les immeubles haussmanniens de Vaugirard, les tours du Front de Seine et les résidences récentes de Javel. Vous avez un doute sur l'état de vos menuiseries ? N'hésitez pas à nous contacter pour un devis gratuit et sans engagement dans tout le 15ᵉ arrondissement.
          </p>

          {/* Image d'en-tête blog */}
          <div className="max-w-3xl mx-auto mb-12">
            <div className="aspect-[16/9] rounded-2xl overflow-hidden">
              <img
                src="/images/dfpro-blog-header.webp"
                alt="Intérieur parisien lumineux avec fenêtres — conseils réparation fenêtres DF PRO Paris 15"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {articles.map((article, idx) => (
              <Link key={article.slug} to={article.slug} className="group bg-brand-mint/20 rounded-xl overflow-hidden card-hover">
                <div className="aspect-[16/10] overflow-hidden">
                  <img
                    src={
                      idx === 0
                        ? "/images/dfpro-blog-thumb-poignee.webp"
                        : idx === 1
                        ? "/images/dfpro-blog-thumb-volet.webp"
                        : "/images/dfpro-blog-thumb-vitrage.webp"
                    }
                    alt={
                      idx === 0
                        ? "Poignée de fenêtre — article réparation poignée DF PRO Paris 15"
                        : idx === 1
                        ? "Volet roulant façade parisienne — article volet bloqué DF PRO Paris 15"
                        : "Double vitrage intérieur parisien — article vitrage embué DF PRO Paris 15"
                    }
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                </div>
                <div className="p-5 md:p-6">
                  <div className="flex items-center gap-2 text-xs text-brand-text/40 mb-3">
                    <span>{article.date}</span>
                    <span>·</span>
                    <Link to={article.serviceLink} className="text-brand-accent hover:underline">{article.service}</Link>
                  </div>
                  <h2 className="font-heading font-bold text-brand-dark text-base md:text-lg mb-2 group-hover:text-brand-accent transition-colors">{article.title}</h2>
                  <p className="text-brand-text/60 text-sm leading-relaxed mb-4">{article.excerpt}</p>
                  <span className="inline-flex items-center gap-1 text-brand-accent font-semibold text-sm group-hover:gap-2 transition-all">
                    Lire l'article <i className="ri-arrow-right-line text-xs"></i>
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
}